import { useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, TabList, TabPanel, Tabs as UITabs } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { EXTENSION_LINK } from "app/pages/sales-intelligence/constants/common";
import { MANAGEMENT_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import { EXTENSION_MANAGEMENT_MANAGE_ROUTE } from "app/pages/sales-intelligence/constants/sl_routes";
import extensionManagementTrackingService
    from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";
import { MANAGEMENT_MY_INTEGRATIONS } from "app/pages/sales-intelligence/pages/management/translations/managementKeys";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    StyledExtensionTab,
    StyledHeader,
    StyledManagementHeader,
    StyledManagementSIBreadcrumbs,
    StyledManagementTabsContainer,
    StyledManagementTitle,
    StyledManagementWrapper,
} from "../SavedSearchWebhooksManagementContainer/styles";

import { ConfigurationTab } from "./ConfigurationTab";
import { DeploymentTab } from "./DeploymentTab";
import { InsightsAndAdoptionTab } from "./InsightsAndAdoptionTab";

const extensionManagementTabDict = {
    insightsAndAdoption: 0,
    configuration: 1,
    deployment: 2,
} as const;

export const ExtensionManagementContainer = () => {
    const { translate } = useTranslate();
    const breadcrumbs = useMemo(
        () => [
            {
                state: MANAGEMENT_ROUTE,
                title: translate(MANAGEMENT_MY_INTEGRATIONS),
            },
            {
                state: EXTENSION_MANAGEMENT_MANAGE_ROUTE,
                title: translate("si.management.extension.title"),
            },
        ],
        [translate],
    );
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(extensionManagementTabDict.insightsAndAdoption);

    const handleBreadcrumbClick = ({ state }: { state: string }) => {
        extensionManagementTrackingService.trackBreadcrumbClick(state);
        void proNavigator.go(state);
    };

    const handleTabSelect = (index: number) => {
        extensionManagementTrackingService.trackTabSwitch(index);
        setSelectedTabIndex(index);
    };

    const handleStoreLink = () => {
        extensionManagementTrackingService.trackChromeWebStoreLink();
        window.open(EXTENSION_LINK, "_blank", "noopener,noreferrer");
    };

    return (
        <StyledManagementWrapper data-automation="sales-intelligence-extension-manage-page">
            <StyledManagementSIBreadcrumbs
                list={breadcrumbs}
                onClick={handleBreadcrumbClick}
                dataAutomation="extension-management-breadcrumbs"
            />
            <Box height="32px" />
            <StyledManagementTabsContainer data-automation="sales-intelligence-extension-manage-body">
                <StyledHeader>
                    <StyledManagementHeader>
                        <StyledManagementTitle>{translate("si.management.extension.title")}</StyledManagementTitle>
                    </StyledManagementHeader>
                    <Flex
                        alignItems="baseline"
                        gap="4px"
                    >
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon["300"]}
                            data-automation="sales-intelligence-extension-manage-description"
                            padding="0 0 4px"
                            flex="1"
                        >
                            {translate("si.management.extension.description")}
                        </Text>
                        <IconButton
                            type="flat"
                            iconName="open-new-window"
                            placement="right"
                            onClick={handleStoreLink}
                            data-automation="sales-intelligence-extension-store-link"
                        >
                            {translate("si.management.extension.store.link.text")}
                        </IconButton>
                    </Flex>
                </StyledHeader>
                <UITabs
                    selectedIndex={selectedTabIndex}
                    onSelect={handleTabSelect}
                >
                    <TabList data-automation="extension-management-tabs">
                        <StyledExtensionTab data-automation="extension-management-tab-insights">
                            <Text fontVariant="tab">{translate("si.management.extension.tab1")}</Text>
                        </StyledExtensionTab>
                        <StyledExtensionTab data-automation="extension-management-tab-configuration">
                            <Text fontVariant="tab">{translate("si.management.extension.tab2")}</Text>
                        </StyledExtensionTab>
                        <StyledExtensionTab data-automation="extension-management-tab-deployment">
                            <Text fontVariant="tab">{translate("si.management.extension.tab3")}</Text>
                        </StyledExtensionTab>
                    </TabList>
                    <TabPanel data-automation="extension-management-panel-insights">
                        <InsightsAndAdoptionTab />
                    </TabPanel>
                    <TabPanel data-automation="extension-management-panel-configuration">
                        <ConfigurationTab />
                    </TabPanel>
                    <TabPanel data-automation="extension-management-panel-deployment">
                        <DeploymentTab />
                    </TabPanel>
                </UITabs>
            </StyledManagementTabsContainer>
        </StyledManagementWrapper>
    );
};
